import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import {
  TOKEN_TYPE,
  getTokenFromCookies,
  clearAllTokens,
  setTokenToCookies,
  setTokenExpiry,
} from '../../../lib/cookies';
import { authAPI } from '../api/api';
import type { SSOUser, TokenPair, User } from '../api/types';
import {
  setLoading,
  setError,
  loginSuccess,
  loginOTPRequired,
  logout as logoutAction,
  clearOTPState as clearOTPStateAction,
} from '../store/authSlice';
import { selectAuth, selectUser, selectIsAuthenticated } from '../store/selectors';
import { getRootDomain } from '../../../lib/domain';

function getCookieDomain(): string | undefined {
  const root = getRootDomain(window.location.hostname);
  return root ? `.${root}` : undefined;
}

function clearStoredTokens(): void {
  // Clear the shared production cookie and any host-only cookie left by an
  // older build.
  clearAllTokens(getCookieDomain());
  clearAllTokens();
}

function mapUser(user: SSOUser): User {
  const displayName = user.display_name ?? user.email;
  return {
    id: user.id,
    email: user.email,
    displayName,
    name: displayName,
    role: user.role,
    avatar: user.avatar,
    isActive: user.inactive_at === 0 && user.locked_until !== -1,
    createdAt: new Date(user.created_at * 1000).toISOString(),
    updatedAt: new Date(user.updated_at * 1000).toISOString(),
  };
}

function saveTokens(tokens: TokenPair): void {
  const domain = getCookieDomain();
  setTokenToCookies(TOKEN_TYPE.ACCESS_TOKEN, tokens.access_token, tokens.expires_in, domain);
  setTokenToCookies(TOKEN_TYPE.REFRESH_TOKEN, tokens.refresh_token, 7 * 24 * 60 * 60, domain);
  setTokenExpiry(new Date(Date.now() + tokens.expires_in * 1000).toISOString(), domain);
}

export const useAuth = () => {
  const dispatch = useDispatch();
  const authState = useSelector(selectAuth);
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkingRef = useRef(false);
  const pendingTotpRef = useRef<{ email: string; password: string } | null>(null);

  const scheduleTokenRefresh = () => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    const expiresAt = getTokenFromCookies(TOKEN_TYPE.EXPIRES_AT);
    if (!expiresAt) return;
    const delay = new Date(expiresAt).getTime() - Date.now() - 30_000;
    if (delay > 0) refreshTimeoutRef.current = setTimeout(() => { void refreshToken(); }, delay);
  };

  const refreshToken = async (): Promise<boolean> => {
    const value = getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN);
    if (!value) {
      clearStoredTokens();
      dispatch(logoutAction());
      return false;
    }
    try {
      const response = await authAPI.refreshToken(value);
      if (!response.success) throw new Error(response.message);
      saveTokens(response.data);
      scheduleTokenRefresh();
      return true;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response && [400, 401, 403].includes(error.response.status)) {
        clearStoredTokens();
        dispatch(logoutAction());
      }
      return false;
    }
  };

  const checkAuth = async () => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    try {
      const accessToken = getTokenFromCookies(TOKEN_TYPE.ACCESS_TOKEN);
      const refreshTokenValue = getTokenFromCookies(TOKEN_TYPE.REFRESH_TOKEN);
      if (!accessToken && !refreshTokenValue) return;
      if (!accessToken && !(await refreshToken())) return;
      scheduleTokenRefresh();
      if (user && isAuthenticated) return;
      const response = await authAPI.me();
      if (!response.success) throw new Error(response.message);
      dispatch(loginSuccess(mapUser(response.data)));
    } catch (error) {
      if (axios.isAxiosError(error) && error.response && [401, 403].includes(error.response.status)) {
        clearStoredTokens();
        dispatch(logoutAction());
      }
    } finally {
      dispatch(setLoading(false));
      checkingRef.current = false;
    }
  };

  useEffect(() => () => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authAPI.login(email, password);
    if (!response.success) {
      dispatch(setError(response.message));
      throw new Error(response.message);
    }
    if (response.messageCode === 'TOTP_REQUIRED') {
      pendingTotpRef.current = { email, password };
      dispatch(loginOTPRequired(email));
      return { success: false, needsOTP: true, email, message: response.message };
    }
    if (response.messageCode !== 'LOGIN_SUCCESS') throw new Error(response.message);
    saveTokens(response.data as TokenPair);
    scheduleTokenRefresh();
    const me = await authAPI.me();
    if (!me.success) throw new Error(me.message);
    const appUser = mapUser(me.data);
    dispatch(loginSuccess(appUser));
    return { success: true, user: appUser };
  };

  const register = async (email: string, password: string, displayName: string) => {
    const response = await authAPI.register(email, password, displayName);
    if (!response.success) {
      dispatch(setError(response.message));
      throw new Error(response.message);
    }
    return { success: true, email, message: response.message };
  };

  const otpLogin = async (email: string, otpCode: string) => {
    if (pendingTotpRef.current?.email === email) {
      const credentials = pendingTotpRef.current;
      const response = await authAPI.totpVerify(email, credentials.password, otpCode);
      if (!response.success) throw new Error(response.message);
      saveTokens(response.data as TokenPair);
      const me = await authAPI.me();
      if (!me.success) throw new Error(me.message);
      pendingTotpRef.current = null;
      dispatch(loginSuccess(mapUser(me.data)));
      return { success: true, user: mapUser(me.data) };
    }

    const response = await authAPI.verifyEmail(email, otpCode);
    if (!response.success) throw new Error(response.message);
    dispatch(clearOTPStateAction());
    return { success: true, activated: true, message: response.message };
  };

  const requestOTP = async (email: string) => {
    const response = await authAPI.resendVerification(email);
    if (!response.success) throw new Error(response.message);
    return { success: true, message: response.message };
  };

  const googleLogin = async (params: { accessToken?: string; idToken?: string }) => {
    const response = await authAPI.googleLogin(params);
    if (!response.success) throw new Error(response.message);
    saveTokens(response.data as TokenPair);
    const me = await authAPI.me();
    if (!me.success) throw new Error(me.message);
    const appUser = mapUser(me.data);
    scheduleTokenRefresh();
    dispatch(loginSuccess(appUser));
    return { success: true, user: appUser };
  };

  const logout = async () => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    try { await authAPI.logout(); } catch { /* local logout still completes */ }
    clearStoredTokens();
    dispatch(logoutAction());
  };

  const hasRole = (requiredRoles: string | string[]) => {
    if (!user) return false;
    const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    return roles.includes(user.role);
  };
  const hasAdminAccess = () => !!user && ['admin', 'super_admin'].includes(user.role);
  const canPerformWrite = hasAdminAccess;
  const clearOTPState = () => dispatch(clearOTPStateAction());

  return {
    ...authState,
    login,
    register,
    otpLogin,
    requestOTP,
    googleLogin,
    logout,
    checkAuth,
    hasRole,
    hasAdminAccess,
    canPerformWrite,
    clearOTPState,
  };
};
